import type {
  BankBudgetReviewReason,
  BankBudgetCategoryId,
  BankFeedTransactionInput,
  CanonicalBudgetCategory,
  MoneyConfidence,
} from './money';

export type AmazonCategoryTarget = {
  label: string;
  budgetCategory: CanonicalBudgetCategory;
  keywords: string[];
};

export type AmazonCategoryRuleScope = 'item' | 'brand' | 'keyword';

export type AmazonCategoryRule = {
  scope: AmazonCategoryRuleScope;
  itemTitle?: string | null;
  brandKey?: string | null;
  keyword?: string | null;
  budgetCategory: CanonicalBudgetCategory;
};

export type AmazonItemClassification = {
  budgetCategory: CanonicalBudgetCategory | null;
  confidence: MoneyConfidence;
  needsReview: boolean;
  reviewReason: BankBudgetReviewReason | null;
  matchedRuleScope: AmazonCategoryRuleScope | null;
};

export type AmazonOrderItem = {
  id?: string;
  title: string;
  brandKey: string | null;
  quantity: number;
  lineTotal: number;
  budgetCategory: CanonicalBudgetCategory | null;
  confidence: MoneyConfidence;
  needsReview: boolean;
  reviewReason: BankBudgetReviewReason | null;
  isSubscribeAndSave: boolean;
};

export type AmazonOrderRecord = {
  id?: string;
  orderNumber: string;
  orderDate: string;
  orderTotal: number;
  currency: string;
  source: 'forwarded_email' | 'pasted_text' | 'historical_upload';
  subject: string | null;
  senderEmail: string | null;
  excerpt: string | null;
  subscribeAndSave: boolean;
  matchedBankTransactionId: string | null;
  matchConfidence: 'high' | 'medium' | 'low';
  matchReason: string | null;
  items: AmazonOrderItem[];
};

export type AmazonOrderParseResult = {
  orderNumber: string;
  orderDate: string;
  orderTotal: number;
  currency: string;
  subscribeAndSave: boolean;
  items: Array<{
    title: string;
    brandKey: string | null;
    quantity: number;
    lineTotal: number;
    isSubscribeAndSave: boolean;
  }>;
};

export type AmazonOrderMatch = {
  orderNumber: string;
  bankTransactionId: string | null;
  matchConfidence: 'high' | 'medium' | 'low';
  matchReason: string | null;
};

export const AMAZON_CATEGORY_TARGETS: AmazonCategoryTarget[] = [
  {
    label: 'Household',
    budgetCategory: 'Food & housekeeping',
    keywords: ['toilet roll', 'kitchen roll', 'detergent', 'washing up', 'cleaning', 'bin bag', 'laundry', 'soap', 'paper towel', 'bleach'],
  },
  {
    label: 'Groceries',
    budgetCategory: 'Food & housekeeping',
    keywords: ['coffee', 'tea', 'cereal', 'rice', 'pasta', 'snack', 'tinned', 'sauce', 'baby food', 'grocer', 'food'],
  },
  {
    label: 'Clothing',
    budgetCategory: 'Clothing & personal care',
    keywords: ['shirt', 't-shirt', 'jumper', 'hoodie', 'trousers', 'jeans', 'dress', 'skirt', 'socks', 'underwear', 'shoes'],
  },
  {
    label: 'Pets',
    budgetCategory: 'Pets',
    keywords: ['pet food', 'cat', 'dog', 'litter', 'treat', 'lead', 'harness', 'flea', 'wormer', 'pet'],
  },
  {
    label: 'Health / Supplements',
    budgetCategory: 'Health & medical',
    keywords: ['vitamin', 'supplement', 'magnesium', 'omega', 'protein', 'medicine', 'health'],
  },
  {
    label: 'Children / School',
    budgetCategory: 'Childcare & school',
    keywords: ['school', 'uniform', 'stationery', 'notebook', 'pencil', 'lunchbox', 'backpack', 'child', 'kids', 'children'],
  },
  {
    label: 'Home',
    budgetCategory: 'Other essential',
    keywords: ['shelf', 'curtain', 'bulb', 'paint', 'tool', 'home', 'storage', 'lamp', 'table', 'chair'],
  },
  {
    label: 'Electronics',
    budgetCategory: 'Other discretionary',
    keywords: ['charger', 'cable', 'usb', 'hdmi', 'adapter', 'headphone', 'speaker', 'monitor', 'keyboard', 'mouse', 'battery', 'plug'],
  },
  {
    label: 'Leisure / Discretionary',
    budgetCategory: 'Leisure / entertainment',
    keywords: ['game', 'book', 'movie', 'dvd', 'toy', 'craft', 'gift', 'leisure', 'entertainment'],
  },
];

const AMAZON_BUDGET_CATEGORY_LOOKUP = new Map<CanonicalBudgetCategory, BankBudgetCategoryId>([
  ['Food & housekeeping', 'food_housekeeping'],
  ['Clothing & personal care', 'clothing_personal_care'],
  ['Pets', 'pets'],
  ['Health & medical', 'health_medical'],
  ['Childcare & school', 'childcare'],
  ['Other essential', 'other_essential'],
  ['Other discretionary', 'other_discretionary'],
  ['Leisure / entertainment', 'leisure_entertainment'],
]);

const AMAZON_RULE_CATEGORY_LOOKUP = new Map(
  AMAZON_CATEGORY_TARGETS.map((target) => [target.budgetCategory, target]),
);

const AMAZON_ORDER_TOTAL_RE = /(?:order\s*total|grand\s*total|total)\s*[:\-]?\s*£?\s*(\d+(?:\.\d{1,2})?)/i;
const AMAZON_ORDER_NUMBER_RE = /\b(?:order\s*(?:number|#)?|order)\s*[:\-]?\s*(\d{3}-\d{7}-\d{7})\b/i;
const AMAZON_DATE_RE = /\b(?:order\s*date|placed\s*on|delivered\s*on|delivery\s*date)\s*[:\-]?\s*(\d{1,2}\s+[A-Za-z]{3,9}\s+\d{4}|\d{4}-\d{2}-\d{2})\b/i;
const MONTH_LOOKUP: Record<string, number> = {
  january: 1,
  february: 2,
  march: 3,
  april: 4,
  may: 5,
  june: 6,
  july: 7,
  august: 8,
  september: 9,
  october: 10,
  november: 11,
  december: 12,
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  sept: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

function normaliseText(value: string) {
  return value
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[^\w£&/.-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normaliseKey(value: string) {
  return normaliseText(value)
    .replace(/\b(?:amazon|co|uk|order|item|qty|quantity|prime|store|marketplace)\b/g, ' ')
    .replace(/[^\w]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractCurrency(value: string) {
  const currencyMatch = value.match(/(?:£|gbp)\s*(\d+(?:\.\d{1,2})?)/i);
  if (currencyMatch) {
    return Number(currencyMatch[1]);
  }

  const bareAmountMatch = value.match(/^\s*(\d+\.\d{1,2})\s*$/);
  return bareAmountMatch ? Number(bareAmountMatch[1]) : null;
}

function parseLooseDate(value: string) {
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  const textualMatch = trimmed.match(/^(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{4})$/);
  if (textualMatch) {
    const day = Number(textualMatch[1]);
    const month = MONTH_LOOKUP[textualMatch[2].toLowerCase()];
    const year = Number(textualMatch[3]);
    if (month && day >= 1 && day <= 31) {
      return `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    }
  }

  const parsed = new Date(trimmed);
  if (Number.isFinite(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }

  return null;
}

function inferOrderDate(text: string) {
  const direct = text.match(AMAZON_DATE_RE);
  if (direct) {
    const parsed = parseLooseDate(direct[1]);
    if (parsed) return parsed;
  }

  const fallback = text.match(/\b(\d{1,2}\s+[A-Za-z]{3,9}\s+\d{4})\b/);
  if (fallback) {
    const parsed = parseLooseDate(fallback[1]);
    if (parsed) return parsed;
  }

  return new Date().toISOString().slice(0, 10);
}

function inferOrderNumber(text: string) {
  const direct = text.match(AMAZON_ORDER_NUMBER_RE);
  if (direct) return direct[1];

  const fallback = text.match(/\b(\d{3}-\d{7}-\d{7})\b/);
  return fallback?.[1] || null;
}

function inferOrderTotal(text: string) {
  const direct = text.match(AMAZON_ORDER_TOTAL_RE);
  if (direct) return Number(direct[1]);

  const currency = extractCurrency(text);
  return currency || null;
}

function inferBrandKey(title: string) {
  const normalised = normaliseKey(title);
  if (!normalised) return null;
  const words = normalised.split(' ').filter(Boolean);
  return words.slice(0, 2).join(' ') || null;
}

function splitItemBlocks(text: string) {
  const normalised = text.replace(/\r\n/g, '\n');
  return normalised
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);
}

function blockLooksLikeOrderItem(block: string) {
  const lower = block.toLowerCase();
  if (!lower) return false;
  if (/(order\s*total|grand\s*total|subtotal|tax|shipping|delivery|dispatch|invoice)/i.test(lower)) {
    return false;
  }
  return /£\s*\d+(?:\.\d{1,2})?/.test(block) || /\bqty\b|\bquantity\b|\bx\d+\b/.test(lower);
}

function parseItemBlock(block: string) {
  const lines = block
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length) return null;

  const price = lines.map((line) => extractCurrency(line)).find((value) => typeof value === 'number' && Number.isFinite(value));
  if (price == null) return null;

  const titleLine = lines.find((line) => !/^(qty|quantity|price|total|amazon|sold by|dispatch|delivery|order|prime)/i.test(line))
    || lines[0];
  const quantityMatch =
    block.match(/\bqty[:\s]*([0-9]+)\b/i)
    || block.match(/\bquantity[:\s]*([0-9]+)\b/i)
    || block.match(/\bx\s*([0-9]+)\b/i);
  const quantity = Math.max(1, Number(quantityMatch?.[1] || 1));
  const title = titleLine.replace(/£\s*\d+(?:\.\d{1,2})?/g, '').replace(/\s+/g, ' ').trim();

  if (!title) return null;

  return {
    title,
    brandKey: inferBrandKey(title),
    quantity,
    lineTotal: Number(price),
    isSubscribeAndSave: false,
  };
}

function parseAmazonItemBlocks(text: string) {
  const blocks = splitItemBlocks(text);
  const items = blocks
    .filter(blockLooksLikeOrderItem)
    .map(parseItemBlock)
    .filter((item): item is NonNullable<ReturnType<typeof parseItemBlock>> => Boolean(item));

  if (items.length > 0) {
    return items;
  }

  const lines = text
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const priceLine = lines.find((line) => /£\s*\d+(?:\.\d{1,2})?/.test(line));
  const titleLine = lines.find((line) => !/(order|total|subtotal|shipping|delivery|dispatch|invoice)/i.test(line));

  if (!priceLine || !titleLine) {
    return [];
  }

  const price = extractCurrency(priceLine) || 0;
  return [{
    title: titleLine.replace(/£\s*\d+(?:\.\d{1,2})?/g, '').trim(),
    brandKey: inferBrandKey(titleLine),
    quantity: 1,
    lineTotal: price,
    isSubscribeAndSave: false,
  }];
}

function spreadOrderDifference(items: AmazonOrderParseResult['items'], orderTotal: number) {
  if (!items.length) return [];
  const rawTotal = items.reduce((sum, item) => sum + Math.max(0, item.lineTotal), 0);
  if (!Number.isFinite(orderTotal) || orderTotal <= 0) {
    return items;
  }

  if (Math.abs(rawTotal - orderTotal) < 0.01) {
    return items;
  }

  if (rawTotal > 0) {
    const factor = orderTotal / rawTotal;
    return items.map((item) => ({
      ...item,
      lineTotal: Number((item.lineTotal * factor).toFixed(2)),
    }));
  }

  const evenSplit = Number((orderTotal / items.length).toFixed(2));
  return items.map((item, index) => ({
    ...item,
    lineTotal: index === items.length - 1 ? Number((orderTotal - evenSplit * (items.length - 1)).toFixed(2)) : evenSplit,
  }));
}

function budgetCategoryToBreakdownId(category: CanonicalBudgetCategory | null): BankBudgetCategoryId | null {
  if (!category) return null;
  return AMAZON_BUDGET_CATEGORY_LOOKUP.get(category) || null;
}

function keywordMatchScore(text: string, keywords: string[]) {
  const normalised = normaliseText(text);
  return keywords.reduce((score, keyword) => score + (normalised.includes(keyword) ? 1 : 0), 0);
}

export function classifyAmazonItem(
  title: string,
  rules: readonly AmazonCategoryRule[] | null | undefined = [],
): AmazonItemClassification {
  const normalisedTitle = normaliseText(title);
  const key = normaliseKey(title);

  const exactRule = rules?.find((rule) => rule.scope === 'item' && normaliseText(rule.itemTitle || '') === normalisedTitle);
  if (exactRule) {
    return {
      budgetCategory: exactRule.budgetCategory,
      confidence: 'high',
      needsReview: false,
      reviewReason: null,
      matchedRuleScope: 'item',
    };
  }

  const brandRule = rules?.find((rule) => rule.scope === 'brand' && normaliseKey(rule.brandKey || '') && key.includes(normaliseKey(rule.brandKey || '')));
  if (brandRule) {
    return {
      budgetCategory: brandRule.budgetCategory,
      confidence: 'medium',
      needsReview: false,
      reviewReason: null,
      matchedRuleScope: 'brand',
    };
  }

  const keywordRule = rules?.find((rule) => rule.scope === 'keyword' && normaliseText(rule.keyword || '') && normalisedTitle.includes(normaliseText(rule.keyword || '')));
  if (keywordRule) {
    return {
      budgetCategory: keywordRule.budgetCategory,
      confidence: 'medium',
      needsReview: false,
      reviewReason: null,
      matchedRuleScope: 'keyword',
    };
  }

  let bestTarget: AmazonCategoryTarget | null = null;
  let bestScore = 0;

  for (const target of AMAZON_CATEGORY_TARGETS) {
    const score = keywordMatchScore(normalisedTitle, target.keywords);
    if (score > bestScore) {
      bestScore = score;
      bestTarget = target;
    }
  }

  if (!bestTarget || bestScore === 0) {
    return {
      budgetCategory: null,
      confidence: 'low',
      needsReview: true,
      reviewReason: 'low-confidence',
      matchedRuleScope: null,
    };
  }

  return {
    budgetCategory: bestTarget.budgetCategory,
    confidence: bestScore >= 2 ? 'high' : 'medium',
    needsReview: bestScore < 2,
    reviewReason: bestScore < 2 ? 'low-confidence' : null,
    matchedRuleScope: null,
  };
}

export function parseAmazonOrderText(
  text: string,
  options: { subject?: string | null; senderEmail?: string | null; source?: AmazonOrderRecord['source'] } = {},
): AmazonOrderParseResult | null {
  const combined = `${options.subject || ''}\n${text}`.trim();
  if (!/amazon|amzn|prime|subscribe & save/i.test(combined)) {
    return null;
  }

  const orderNumber = inferOrderNumber(combined) || `amazon-${Math.random().toString(36).slice(2, 10)}`;
  const orderDate = inferOrderDate(combined);
  const orderTotal = inferOrderTotal(combined) || 0;
  const subscribeAndSave = /subscribe\s*&\s*save|subscribed\s*&\s*save|subscribe and save/i.test(combined);
  const parsedItems = parseAmazonItemBlocks(combined);
  const items = spreadOrderDifference(parsedItems, orderTotal).map((item) => ({
    ...item,
    isSubscribeAndSave: subscribeAndSave || /subscribe\s*&\s*save|subscribed\s*&\s*save/i.test(item.title),
  }));

  return {
    orderNumber,
    orderDate,
    orderTotal,
    currency: 'GBP',
    subscribeAndSave,
    items,
  };
}

export function buildAmazonOrderRecord(
  parsed: AmazonOrderParseResult,
  rules: readonly AmazonCategoryRule[] | null | undefined = [],
  options: {
    id?: string;
    subject?: string | null;
    senderEmail?: string | null;
    excerpt?: string | null;
    source?: AmazonOrderRecord['source'];
  } = {},
): AmazonOrderRecord {
  const items = parsed.items.map((item) => {
    const classification = classifyAmazonItem(item.title, rules);
    return {
      title: item.title,
      brandKey: item.brandKey,
      quantity: item.quantity,
      lineTotal: Number(item.lineTotal.toFixed(2)),
      budgetCategory: classification.budgetCategory,
      confidence: classification.confidence,
      needsReview: classification.needsReview,
      reviewReason: classification.reviewReason,
      isSubscribeAndSave: item.isSubscribeAndSave,
    } satisfies AmazonOrderItem;
  });

  return {
    id: options.id,
    orderNumber: parsed.orderNumber,
    orderDate: parsed.orderDate,
    orderTotal: Number(parsed.orderTotal.toFixed(2)),
    currency: parsed.currency,
    source: options.source || 'forwarded_email',
    subject: options.subject || null,
    senderEmail: options.senderEmail || null,
    excerpt: options.excerpt || null,
    subscribeAndSave: parsed.subscribeAndSave,
    matchedBankTransactionId: null,
    matchConfidence: 'low',
    matchReason: null,
    items,
  };
}

export function buildAmazonSyntheticTransactions(
  orders: readonly AmazonOrderRecord[],
): BankFeedTransactionInput[] {
  return orders.flatMap((order) =>
    order.items.map((item, index) => {
      const breakdownCategoryId = budgetCategoryToBreakdownId(item.budgetCategory);
      return {
        transactionId: order.id ? `${order.id}:${index}` : `${order.orderNumber}:${index}`,
        bookingDate: order.orderDate,
        amount: Number((-Math.abs(item.lineTotal)).toFixed(2)),
        currency: order.currency || 'GBP',
        merchantName: item.title,
        description: `Amazon order ${order.orderNumber}${item.brandKey ? ` · ${item.brandKey}` : ''}`,
        categoryId: breakdownCategoryId,
        categoryLabel: item.budgetCategory || null,
        confidence: item.confidence,
        needsReview: item.needsReview,
        reviewReason: item.needsReview ? 'low-confidence' : item.reviewReason,
        source: 'amazon',
      } satisfies BankFeedTransactionInput;
    }));
}

export function mergeBankFeedInputsWithAmazonOrders(
  bankTransactions: readonly BankFeedTransactionInput[],
  orders: readonly AmazonOrderRecord[],
): BankFeedTransactionInput[] {
  const matchedBankTransactionIds = new Set(
    orders
      .map((order) => order.matchedBankTransactionId)
      .filter((value): value is string => Boolean(value)),
  );
  const amazonSyntheticTransactions = buildAmazonSyntheticTransactions(orders);

  if (!matchedBankTransactionIds.size) {
    return [...bankTransactions, ...amazonSyntheticTransactions];
  }

  return [
    ...bankTransactions.filter((transaction) =>
      !transaction.transactionId || !matchedBankTransactionIds.has(transaction.transactionId),
    ),
    ...amazonSyntheticTransactions,
  ];
}

export function reconcileAmazonOrdersToBankTransactions(
  orders: readonly AmazonOrderRecord[],
  bankTransactions: readonly {
    id: string;
    booking_date: string;
    amount: number;
    description?: string | null;
    creditor_name?: string | null;
    debtor_name?: string | null;
  }[],
): AmazonOrderMatch[] {
  const candidateTransactions = bankTransactions.filter((transaction) =>
    transaction.amount < 0 && /amazon|amzn|prime/i.test(`${transaction.description || ''} ${transaction.creditor_name || ''} ${transaction.debtor_name || ''}`),
  );

  return orders.map((order) => {
    let best: { score: number; tx: { id: string; booking_date: string; amount: number; description?: string | null; creditor_name?: string | null; debtor_name?: string | null } } | null = null;

    for (const transaction of candidateTransactions) {
      const amountDiff = Math.abs(Math.abs(transaction.amount) - order.orderTotal);
      if (amountDiff > Math.max(1.5, order.orderTotal * 0.02)) {
        continue;
      }

      const dateDiff = Math.abs((new Date(order.orderDate).getTime() - new Date(transaction.booking_date).getTime()) / (1000 * 60 * 60 * 24));
      if (dateDiff > 10) {
        continue;
      }

      const amountScore = Math.max(0, 10 - amountDiff * 3);
      const dateScore = Math.max(0, 10 - dateDiff);
      const merchantScore = /amazon|amzn|prime/i.test(`${transaction.description || ''} ${transaction.creditor_name || ''}`) ? 8 : 2;
      const score = amountScore + dateScore + merchantScore;

      if (!best || score > best.score) {
        best = { score, tx: transaction };
      }
    }

    if (!best) {
      return {
        orderNumber: order.orderNumber,
        bankTransactionId: null,
        matchConfidence: 'low',
        matchReason: 'No bank transaction matched closely enough.',
      };
    }

    return {
      orderNumber: order.orderNumber,
      bankTransactionId: best.tx.id,
      matchConfidence: best.score >= 22 ? 'high' : best.score >= 16 ? 'medium' : 'low',
      matchReason: `Matched on amount and date within tolerance.`,
    };
  });
}

export function toAmazonCategoryRuleInput(
  budgetCategory: CanonicalBudgetCategory,
  scope: AmazonCategoryRuleScope,
  value: string,
) {
  return {
    scope,
    item_title: scope === 'item' ? value : null,
    brand_key: scope === 'brand' ? value : null,
    keyword: scope === 'keyword' ? value : null,
    budget_category: budgetCategory,
  };
}

export function amazonCategoryToBreakdownId(category: CanonicalBudgetCategory | null | undefined) {
  return budgetCategoryToBreakdownId(category || null);
}

export function buildAmazonOrderSummary(orders: readonly AmazonOrderRecord[]) {
  const total = orders.reduce((sum, order) => sum + order.orderTotal, 0);
  const lineCount = orders.reduce((sum, order) => sum + order.items.length, 0);
  const reviewCount = orders.reduce((sum, order) => sum + order.items.filter((item) => item.needsReview).length, 0);
  const matchedCount = orders.filter((order) => Boolean(order.matchedBankTransactionId)).length;
  const subscribeAndSaveCount = orders.filter((order) => order.subscribeAndSave).length;

  return {
    total,
    lineCount,
    reviewCount,
    matchedCount,
    subscribeAndSaveCount,
  };
}

export function mapAmazonBudgetCategoryLabel(category: CanonicalBudgetCategory | null | undefined) {
  return category || 'Needs review';
}

export function isAmazonOrderRecord(value: unknown): value is AmazonOrderRecord {
  return Boolean(value && typeof value === 'object' && 'orderNumber' in value && 'items' in value);
}
